import { OrderItemType, OrderItemTypeCodeById } from '@/types/orderItemType'
import type {
  TCartLine,
  TWordwareLunchCartLine,
  TSkywardLunchCartLine,
  TTuitionContractCartLine,
  TStudentFeeSimpleCartLine,
  TActivityCartLine,
  TAffinetyActivityCartLine,
  TMbaPowerschoolFeeCartLine,
  TCorePowerschoolFeeCartLine,
  TMbaCafeLunchCartLine,
  TDestinyFineCartLine,
  TIncidentIqFeeCartLine,
  TFamilyUserInvoiceCartLine,
  TInvoiceCartLine,
  TEPayTrakFeeCartLine
} from '@/types/cart'
import { Util } from '@/modules/util'

/**
 * Formats cart item objects into formatted payments to be passed to the server.
 */
export default class PaymentFormatter {
  /**
   * Order item types the server settles as a generic invoice payment. TUITION is
   * deliberately absent — contract invoices carry revision and scheduled payment
   * context and keep their own branch in the switch below.
   */
  private static readonly GENERIC_INVOICE_TYPES: OrderItemType[] = [
    OrderItemType.OtherFee,
    OrderItemType.Activity,
    OrderItemType.EpaytrakCartItem,
    OrderItemType.Childcare,
    OrderItemType.AdditionalCharge
  ]

  static format(cartItems: TCartLine[]): Record<string, TFormattedPayment[]> {
    let formattedPayments: Record<string, TFormattedPayment[]> = {}
    cartItems.forEach(cartItem => {
      let paymentItem = PaymentFormatter.formatCartItem(cartItem)

      if (cartItem.hasOwnProperty('description')) {
        paymentItem.description = cartItem.description
      }
      if (cartItem.hasOwnProperty('amount')) {
        paymentItem.amount = cartItem.amount
      }

      if (!formattedPayments.hasOwnProperty(paymentItem.fee_category)) {
        formattedPayments[paymentItem.fee_category] = []
      }
      formattedPayments[paymentItem.fee_category].push(paymentItem)
    })
    return formattedPayments
  }

  /**
   * A family-user invoice is paid the same way no matter what it was raised for:
   * "put $X against invoice #N". Only the fee_category varies, and it has to stay
   * the invoice's OWN order item type — the checkout stamps that onto the order
   * line, and month-end finance buckets revenue off it (MonthEndFinanceScript
   * groups on TUITION/CHILDCARE), so flattening every invoice to additional_charge
   * would misreport it.
   *
   * Tuition contract invoices are excluded: they carry contract revision and
   * scheduled payment context and keep the richer branch in the switch below.
   */
  private static asGenericInvoiceLine(cartItem: TCartLine): TFamilyUserInvoiceCartLine | null {
    const invoiceLine = cartItem as TFamilyUserInvoiceCartLine

    if (invoiceLine.isInvoice !== true || invoiceLine.invoiceHeaderId === undefined) {
      return null
    }

    // Constrained to the types the server actually settles, rather than to "any
    // type that happens to be in the enum". A line outside this set would sail
    // through the fee_category presence check, skip or fail its own type validator,
    // and never be linked to its invoice — charged and silently unsettled. Mirrors
    // PostPaymentProcessingPhase::INVOICE_PAYABLE_ORDER_ITEM_TYPES in edutrak-core;
    // tuition is absent on purpose and keeps the branch below.
    return PaymentFormatter.GENERIC_INVOICE_TYPES.includes(cartItem.orderItemType)
      ? invoiceLine
      : null
  }

  private static formatCartItem(cartItem: TCartLine): TFormattedPayment {
    let paymentItem = { uniqueId: cartItem.uniqueId } as TFormattedPayment

    const invoiceLine = PaymentFormatter.asGenericInvoiceLine(cartItem)
    if (invoiceLine !== null) {
      paymentItem.fee_id = invoiceLine.invoiceHeaderId
      paymentItem.fee_category = OrderItemTypeCodeById[invoiceLine.orderItemType as OrderItemType]
      paymentItem.is_invoice = true
      if (invoiceLine.familyAccountId !== undefined) {
        paymentItem.family_account_id = invoiceLine.familyAccountId
      }
      return paymentItem
    }

    switch (cartItem.orderItemType) {
      case OrderItemType.WordwareLunch: {
        let wordwareItem = cartItem as TWordwareLunchCartLine
        paymentItem.fee_id = wordwareItem.familyAccountId
        paymentItem.fee_category = 'wordware_lunch'
        paymentItem.family_account_id = wordwareItem.familyAccountId
        break
      }
      case OrderItemType.SkywardLunch: {
        let skywardLunchItem = cartItem as TSkywardLunchCartLine
        paymentItem.fee_category = 'skyward_lunch'
        paymentItem.student_account_id = skywardLunchItem.studentAccountId
        break
      }
      case OrderItemType.TuitionContract: {
        let tuitionContractItem = cartItem as TTuitionContractCartLine
        // DTA-539: a non-invoice tuition line with no scheduled payment id is the
        // contract's license fee (see TuitionContractSection / ContractRevisionPaymentModal,
        // which key off scheduledPaymentId === null). It must be tagged tuition_license so
        // the checkout routes it to process_tuition_license and marks the license paid —
        // otherwise it is billed as a regular contract payment and the license stays unpaid.
        const isLicense =
          !tuitionContractItem.isInvoice && tuitionContractItem.scheduledPaymentId === null
        paymentItem.fee_id = tuitionContractItem.isInvoice
          ? (tuitionContractItem.invoiceHeaderId ?? tuitionContractItem.contractRevisionId)
          : tuitionContractItem.contractRevisionId
        paymentItem.fee_category = isLicense ? 'tuition_license' : 'tuition_contract'
        paymentItem.student_account_id = tuitionContractItem.studentAccountId
        paymentItem.scheduled_payment_id = tuitionContractItem.scheduledPaymentId
        paymentItem.is_invoice = tuitionContractItem.isInvoice ?? false
        break
      }
      case OrderItemType.OtherFee: {
        let studentFeeSimpleItem = cartItem as TStudentFeeSimpleCartLine
        paymentItem.fee_id = studentFeeSimpleItem.feeId
        paymentItem.fee_category = 'other_fee'
        paymentItem.student_account_id = studentFeeSimpleItem.studentAccountId
        paymentItem.family_max_id = studentFeeSimpleItem.familyMaxId
        paymentItem.family_account_id = studentFeeSimpleItem.familyAccountId
        paymentItem.is_invoice = false
        paymentItem.is_partial = studentFeeSimpleItem.isPartial ?? false
        break
      }
      case OrderItemType.AssignedDeposit: {
        let studentFeeSimpleItem = cartItem as TStudentFeeSimpleCartLine
        paymentItem.fee_id = studentFeeSimpleItem.feeId
        paymentItem.fee_category = 'assigned_deposit'
        paymentItem.student_account_id = studentFeeSimpleItem.studentAccountId
        paymentItem.family_max_id = studentFeeSimpleItem.familyMaxId
        paymentItem.family_account_id = studentFeeSimpleItem.familyAccountId
        paymentItem.is_invoice = false
        paymentItem.is_partial = studentFeeSimpleItem.isPartial ?? false
        break
      }
      case OrderItemType.DestinyFine: {
        let destinyFine = cartItem as TDestinyFineCartLine
        paymentItem.fee_id = destinyFine.feeId
        paymentItem.fee_category = 'destiny_fine'
        paymentItem.student_account_id = destinyFine.studentAccountId
        paymentItem.is_invoice = false
        paymentItem.is_partial = destinyFine.isPartial ?? false
        break
      }
      case OrderItemType.IncidentIqFee: {
        let incidentIqFee = cartItem as TIncidentIqFeeCartLine
        paymentItem.fee_id = incidentIqFee.feeId
        paymentItem.fee_category = 'incident_iq_fee'
        paymentItem.student_account_id = incidentIqFee.studentAccountId
        paymentItem.is_invoice = false
        paymentItem.is_partial = incidentIqFee.isPartial ?? false
        // The fee has no assigned-fee row to attach a form submission to until
        // it is paid, so the submission travels with the payment instead.
        paymentItem.form_submission_id = incidentIqFee.formSubmissionId ?? null
        break
      }
      case OrderItemType.AffinetyActivityFee: {
        let activityItem = cartItem as TAffinetyActivityCartLine
        paymentItem.fee_id = activityItem.feeId
        paymentItem.fee_category = 'affinety_activity_fee'
        paymentItem.student_account_id = activityItem.studentAccountId
        break
      }
      case OrderItemType.Activity: {
        let activityItem = cartItem as TActivityCartLine
        paymentItem.fee_id = activityItem.feeId
        paymentItem.fee_category = 'activity'
        paymentItem.student_account_id = activityItem.studentAccountId
        break
      }
      case OrderItemType.AdditionalCharge: {
        let additionalChargeItem = cartItem as TFamilyUserInvoiceCartLine
        paymentItem.fee_id = additionalChargeItem.invoiceHeaderId
        paymentItem.fee_category = 'additional_charge'
        paymentItem.is_invoice = additionalChargeItem.isInvoice ?? false
        break
      }
      case OrderItemType.EpaytrakCartFee: {
        let epaytrakCartFeeItem = cartItem as TEPayTrakFeeCartLine
        paymentItem.fee_id = epaytrakCartFeeItem.feeId
        paymentItem.fee_category = 'epaytrak_cart_fee'
        break
      }
      case OrderItemType.MbaPowerschoolFee: {
        let mbaPowerschoolCartItem = cartItem as TMbaPowerschoolFeeCartLine
        const { feeName, feeDescription, dateCreated, ...filteredData } = mbaPowerschoolCartItem

        paymentItem = {
          ...Util.Obj.keysToSnake(filteredData),
          name: feeName,
          description: feeDescription,
          fee_category: 'mba_powerschool_fee'
        }
        break
      }
      case OrderItemType.CorePowerschoolFee: {
        let corePowerschoolCartItem = cartItem as TCorePowerschoolFeeCartLine
        const { feeName, feeDescription, dateCreated, ...filteredData } = corePowerschoolCartItem

        paymentItem = {
          ...Util.Obj.keysToSnake(filteredData),
          name: feeName,
          description: feeDescription,
          fee_category: 'core_powerschool_fee'
        }
        break
      }
      case OrderItemType.MbaCafeLunch: {
        let mbaCafeLunchCartItem = cartItem as TMbaCafeLunchCartLine
        const { feeName, ...filteredData } = mbaCafeLunchCartItem

        paymentItem = {
          ...Util.Obj.keysToSnake(filteredData),
          name: feeName,
          description: feeName,
          fee_category: 'mba_cafe_lunch'
        }
        break
      }
      default:
        // Leaving fee_category unset groups the line under the literal key
        // "undefined" and the checkout rejects the whole cart with a bare
        // "A payment is missing a fee_category in the payload." Say which type
        // is unmapped so the next one is a one-line fix, not an investigation.
        console.error(
          `PaymentFormatter: no fee_category mapping for order item type ` +
            `${cartItem.orderItemType} (cart line ${cartItem.uniqueId}). ` +
            `The checkout will reject this payment.`
        )
    }

    return paymentItem
  }
}
